import { Subject } from 'rxjs';
import React from 'react';
import { getRandomItem } from './utils/helpers';
import { StarIcon } from './components/AISolutions/solution-icons/StarIcon';
import { ExplainIcon } from './components/AISolutions/solution-icons/ExplainIcon';
import { FindBugIcon } from './components/AISolutions/solution-icons/FindBugIcon';
import { SolutionIcon } from './components/AISolutions/solution-icons/SolutionIcon';
import { Role } from './global-interface';

export const SlAIChatContext = 'mf_SlAIChat_context';
export const SlAISolutionsContext = 'mf_SlAISolutions_context';

export const aISolutionsUnsubscriber$ = new Subject<void>();
export const aIChatUnsubscriber$ = new Subject<void>();

export const messageSuggestions = [
  getRandomItem([
    'What’s the best language to start with?',
    'Which language should I learn first?',
  ]),
  'What’s a programming language?',
  getRandomItem([
    'Why is my code not working?',
    'Write me some code that…',
    'Can you make this code simpler?',
    'What does this code do?',
  ]),
  getRandomItem([
    'Will an artificial intelligence like you steal my job, Kodie?',
    'Can you help me build an AI like you?',
  ]),
];

export const aIChatPagePath = '/kodie-the-ai-coding-chatbot';
export const aILandingPagePath = '/learn-with-ai';

const initialAssistantMessagesVariants = [
  ['Hi! I’m here to help you with all things tech.', 'What’s up?'],
  [
    'Kodie, at your service!',
    'What tech-related matter can I help you with today?',
  ],
  ['Hi 👋', 'Got a question for me about coding or tech?', 'Ask away!'],
  [
    'Hey! Kodie here 👋',
    'Got a tech or code-related question I can help with?',
    'Try me',
  ],
  [
    'Hey there',
    'How can I help?',
    '(If it’s about coding or tech, I’ve got this!)',
  ],
];

export const initialAssistantMessages = getRandomItem(
  initialAssistantMessagesVariants,
).map((message) => ({
  message,
  role: Role.Predefined,
}));

export const warningOnNavigation = 'Kodie’s smart, but has a memory like a fish. Make sure you’ve copied anything you want to keep from the conversation.';

export const testUserHint = '[solo]';

export const notAvailablePath = '/kodie-out-of-town';

export const somethingWentWrong = 'Looks like something went wrong. Please try again.';

export const limitError = 'Sorry about this, my brain’s still a bit fried after all the questions. I’ll just rest for a little while.';

export enum ErrorCode {
  Limit = 429,
}

export enum SolutionActionType {
  ExplainCode = 1,
  FindBug = 2,
  Hint = 3,
  GetSolution = 5,
  AlternativeLessonExplanation = 6,
  RealLifeExamples = 7,
  QuestionHint = 8,
}

export const solutionsData = {
  [SolutionActionType.ExplainCode]: {
    icon: <ExplainIcon />,
    title: 'web-kodie.solution-explain-code',
  },
  [SolutionActionType.FindBug]: {
    icon: <FindBugIcon />,
    title: 'web-kodie.solution-find-bug',
  },
  [SolutionActionType.Hint]: {
    icon: <StarIcon />,
    title: 'web-kodie.solution-hint',
  },
  [SolutionActionType.GetSolution]: {
    icon: <SolutionIcon />,
    title: 'web-kodie.solution-get',
  },
  [SolutionActionType.AlternativeLessonExplanation]: {
    icon: <ExplainIcon />,
    title: 'web-kodie.solution-alternative-explanation',
  },
  [SolutionActionType.RealLifeExamples]: {
    icon: <StarIcon />,
    title: 'web-kodie.solution-examples',
  },
  [SolutionActionType.QuestionHint]: {
    icon: <FindBugIcon />,
    title: 'web-kodie.solution-question-hint',
  },
};

export const aISolutionLoadingMessagesInPractice = [
  'web-kodie.practice-solution-loading-message0',
  'web-kodie.practice-solution-loading-message1',
  'web-kodie.practice-solution-loading-message2',
  'web-kodie.practice-solution-loading-message3',
];

export const aISolutionLoadingMessagesInLesson = [
  'web-kodie.lesson-solution-loading-message0',
  'web-kodie.lesson-solution-loading-message1',
  'web-kodie.lesson-solution-loading-message2',
  'web-kodie.lesson-solution-loading-message3',
];

export const aISolutionErrorMessages = {
  449: {
    isBackToCodeButtonVisible: true,
    isTryAgainButtonVisible: false,
    errorMessage: 'web-kodie.no-code-error-message',
  },
  413: {
    isBackToCodeButtonVisible: true,
    isTryAgainButtonVisible: false,
    errorMessage: 'web-kodie.long-code-error-message',
  },
  1020: {
    isBackToCodeButtonVisible: false,
    isTryAgainButtonVisible: true,
    errorMessage: 'web-kodie.no-internet-error-message',
  },
  422: {
    isBackToCodeButtonVisible: false,
    isTryAgainButtonVisible: false,
    errorMessage: 'web-kodie.content-not-allowed-error-message',
  },
  notDefined: {
    isBackToCodeButtonVisible: false,
    isTryAgainButtonVisible: true,
    errorMessage: 'web-kodie.something-went-wrong-error-message',
  },
};

export enum ContentType {
  RichText = 1,
  CodeSnippet,
}

export enum AssistanceTypes {
  ExplainTheCode = 1,
  FindTheBug,
  CodeHint,
  ExplainTheError,
  ExplainTheSolution,
  AlternativeLessonExplanation,
  RealLifeExamples,
  QuestionHint,
  AnswerExplanation,
}

export enum SolutionContext {
  Practice = 1,
  Lesson = 2,
  Answer = 3,
}

export enum SolutionType {
  SidePanel,
  AnswerExplanation,
}
